/**
 * State Portal demo analytics — feature interest ratings + section enter tracking.
 * Fire-and-forget; never blocks navigation.
 */

import axios from 'axios';

const INTEREST_LOG_KEY = 'statePortalFeatureInterestLog';
const SESSION_ID_KEY = 'statePortalAnalyticsSessionId';
const DEDUPE_MS = 2000;

let lastEnterKey = '';
let lastEnterAt = 0;

function baseLink() {
  return String(import.meta.env.VITE_BASE_LINK || '').replace(/\/$/, '');
}

function optionalString(value, maxLength = 180) {
  return String(value ?? '').trim().slice(0, maxLength);
}

export const FEATURE_INTEREST_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const FEATURE_INTEREST_MAX_FILES = 3;

function normalizeInterestAttachments(list = []) {
  if (!Array.isArray(list)) return [];
  return list.slice(0, FEATURE_INTEREST_MAX_FILES).map((item) => {
    const kind = optionalString(item?.kind, 20).toLowerCase();
    if (!['image', 'file', 'sticker', 'gif'].includes(kind)) return null;
    const fileUrl = optionalString(item?.fileUrl, 500);
    return {
      kind,
      name: optionalString(item?.name, 180),
      mime: optionalString(item?.mime, 120),
      size: Number(item?.size) || 0,
      emoji: optionalString(item?.emoji, 16),
      fileKey: optionalString(item?.fileKey, 400),
      fileUrl: fileUrl.startsWith('http://') || fileUrl.startsWith('https://') ? fileUrl : '',
    };
  }).filter(Boolean);
}

export async function uploadFeatureInterestFile(file) {
  if (!file) return { uploaded: false, skipped: true };
  if (file.size > FEATURE_INTEREST_MAX_FILE_BYTES) {
    return {
      uploaded: false,
      error: true,
      message: `Each file must be ${FEATURE_INTEREST_MAX_FILE_BYTES / (1024 * 1024)} MB or smaller`,
    };
  }
  const form = new FormData();
  form.append('file', file);
  try {
    const response = await axios.post(
      `${baseLink()}/upload-state-portal-feature-interest-file`,
      form,
      { withCredentials: true },
    );
    if (response?.data?.uploaded && response?.data?.attachment) {
      return { uploaded: true, attachment: response.data.attachment };
    }
    return {
      uploaded: false,
      error: true,
      message: response?.data?.error || 'Couldn’t upload that file.',
    };
  } catch (error) {
    console.error('Error uploading state portal feature interest file:', error);
    return {
      uploaded: false,
      error: true,
      message: error?.response?.data?.error || 'Couldn’t upload that file.',
    };
  }
}

function portalContext(mainStore = {}, overrides = {}) {
  const user = mainStore?.user || {};
  const portal = mainStore?.currentStatePortal || {};
  const orgId = optionalString(
    overrides.organizationId
      || portal?.organizationId
      || portal?.companyID
      || '',
    80,
  );
  return {
    state: optionalString(
      overrides.state
        || portal?.abbreviation
        || mainStore?.user?.currentStatePortal
        || '',
      40,
    ).toUpperCase(),
    sport: optionalString(overrides.sport || '', 80),
    organizationId: orgId,
    organizationName: optionalString(
      overrides.organizationName
        || portal?.name
        || portal?.organizationName
        || '',
    ),
    submittedBy: {
      userId: optionalString(user._id || user.id || '', 80),
      name: optionalString(user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()),
      email: optionalString(user.email || '').toLowerCase(),
    },
  };
}

export function getStatePortalAnalyticsSessionId() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return `sp-session-${Date.now()}`;
  }
  try {
    const existing = String(window.sessionStorage.getItem(SESSION_ID_KEY) || '').trim();
    if (existing) return existing;
    const next = `sp-session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch (_) {
    return `sp-session-${Date.now()}`;
  }
}

function cacheInterestLocally(entry) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const existing = JSON.parse(window.localStorage.getItem(INTEREST_LOG_KEY) || '[]');
    const log = Array.isArray(existing) ? existing : [];
    const feature = optionalString(entry?.feature, 120).toLowerCase();
    const state = optionalString(entry?.state, 40).toUpperCase();
    const userId = optionalString(entry?.submittedBy?.userId, 80);
    const next = log.filter((item) => !(
      optionalString(item?.feature, 120).toLowerCase() === feature
      && optionalString(item?.state, 40).toUpperCase() === state
      && optionalString(item?.submittedBy?.userId, 80) === userId
    ));
    next.push(entry);
    window.localStorage.setItem(INTEREST_LOG_KEY, JSON.stringify(next.slice(-100)));
  } catch (_) {
    /* ignore */
  }
}

function readCachedInterest({ feature = '', state = '', userId = '' } = {}) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const featureKey = optionalString(feature, 120).toLowerCase();
    const stateCode = optionalString(state, 40).toUpperCase();
    const uid = optionalString(userId, 80);
    if (!featureKey || !uid) return null;
    const existing = JSON.parse(window.localStorage.getItem(INTEREST_LOG_KEY) || '[]');
    const log = Array.isArray(existing) ? existing : [];
    const match = [...log].reverse().find((item) => (
      optionalString(item?.feature, 120).toLowerCase() === featureKey
      && optionalString(item?.submittedBy?.userId, 80) === uid
      && (!stateCode || optionalString(item?.state, 40).toUpperCase() === stateCode)
    ));
    if (!match) return null;
    const rating = Number(match.rating);
    if (!Number.isInteger(rating) || rating < 0 || rating > 5) return null;
    return {
      feature: featureKey,
      rating,
      feedback: optionalString(match.feedback, 2000),
      attachments: normalizeInterestAttachments(match.attachments),
      state: optionalString(match.state, 40).toUpperCase(),
      createdAt: match.submittedAt || match.createdAt || null,
    };
  } catch (_) {
    return null;
  }
}

/**
 * Load this user's existing rating for a feature + state (if any).
 */
export async function getFeatureInterestForUser({
  mainStore = {},
  feature = '',
  state = '',
} = {}) {
  const featureKey = optionalString(feature, 120).toLowerCase();
  if (!featureKey) return { rated: false, entry: null };

  const context = portalContext(mainStore, { state });
  const cached = readCachedInterest({
    feature: featureKey,
    state: context.state,
    userId: context.submittedBy.userId,
  });
  if (cached) {
    return { rated: true, entry: cached, fromCache: true };
  }

  if (!context.submittedBy.userId) {
    return { rated: false, entry: null };
  }

  try {
    const response = await axios.post(
      `${baseLink()}/get-state-portal-feature-interest-for-user`,
      {
        feature: featureKey,
        state: context.state,
        userId: context.submittedBy.userId,
      },
    );
    if (response?.data?.rated && response?.data?.entry) {
      cacheInterestLocally({
        ...response.data.entry,
        submittedBy: context.submittedBy,
      });
      return {
        rated: true,
        entry: response.data.entry,
      };
    }
    return { rated: false, entry: null };
  } catch (error) {
    console.error('Error loading state portal feature interest for user:', error);
    return { rated: false, entry: null, error: true };
  }
}

/**
 * Record a 0–5 interest rating for a coming-soon / demo feature.
 * One rating per user + feature + state — re-submits are rejected.
 */
export async function recordFeatureInterest({
  mainStore = {},
  feature = '',
  featureLabel = '',
  surface = 'state-portal-coming-soon',
  rating = null,
  feedback = '',
  attachments = [],
  sport = '',
  organizationId = '',
  organizationName = '',
  state = '',
} = {}) {
  const featureKey = optionalString(feature, 120).toLowerCase();
  const score = Number(rating);
  if (!featureKey || !Number.isInteger(score) || score < 0 || score > 5) {
    return { recorded: false, skipped: true };
  }

  const context = portalContext(mainStore, {
    state,
    sport,
    organizationId,
    organizationName,
  });

  const existing = await getFeatureInterestForUser({
    mainStore,
    feature: featureKey,
    state: context.state,
  });
  if (existing?.rated && existing?.entry) {
    return {
      recorded: false,
      alreadyRated: true,
      entry: existing.entry,
    };
  }

  const entry = {
    feature: featureKey,
    featureLabel: optionalString(featureLabel) || featureKey,
    surface: optionalString(surface, 80) || 'state-portal-coming-soon',
    rating: score,
    feedback: optionalString(feedback, 2000),
    attachments: normalizeInterestAttachments(attachments),
    ...context,
    submittedAt: new Date().toISOString(),
  };

  try {
    const response = await axios.post(
      `${baseLink()}/record-state-portal-feature-interest`,
      entry,
    );
    if (response?.data?.alreadyRated) {
      if (response?.data?.entry) {
        cacheInterestLocally({
          ...entry,
          rating: response.data.entry.rating,
          submittedAt: response.data.entry.createdAt,
        });
      }
      return {
        recorded: false,
        alreadyRated: true,
        entry: response?.data?.entry || null,
      };
    }
    if (response?.data?.recorded) {
      cacheInterestLocally(entry);
      return {
        recorded: true,
        updated: Boolean(response?.data?.updated),
        entry: response?.data?.entry || null,
      };
    }
    return { recorded: false, error: true };
  } catch (error) {
    console.error('Error recording state portal feature interest:', error);
    return { recorded: false, error: true };
  }
}

/**
 * Track a section / subsection enter. Dedupes identical keys within 2s.
 */
export async function trackSectionEnter({
  mainStore = {},
  section = '',
  subsection = '',
  surface = 'state-portal-shell',
  label = '',
  path = '',
  state = '',
  organizationId = '',
  organizationName = '',
} = {}) {
  const sectionKey = optionalString(section, 80).toLowerCase() || 'unknown';
  const subsectionKey = optionalString(subsection, 80).toLowerCase();
  const dedupeKey = `${sectionKey}::${subsectionKey}`;
  const now = Date.now();
  if (dedupeKey === lastEnterKey && now - lastEnterAt < DEDUPE_MS) {
    return { recorded: false, deduped: true };
  }
  lastEnterKey = dedupeKey;
  lastEnterAt = now;

  return enqueueActivityEvent({
    mainStore,
    event: 'section_enter',
    section: sectionKey,
    subsection: subsectionKey,
    surface,
    label,
    path: path || (typeof window !== 'undefined' ? window.location?.pathname : ''),
    state,
    organizationId,
    organizationName,
  });
}

const ACTIVITY_FLUSH_MS = 2500;
const ACTIVITY_BATCH_MAX = 40;
let activityQueue = [];
let activityFlushTimer = null;
let activityCaptureInstalled = false;
let activityCaptureOptions = null;
let activityClickHandler = null;

function scheduleActivityFlush() {
  if (activityFlushTimer) return;
  activityFlushTimer = setTimeout(() => {
    activityFlushTimer = null;
    flushActivityQueue();
  }, ACTIVITY_FLUSH_MS);
}

async function flushActivityQueue() {
  if (!activityQueue.length) return;
  const batch = activityQueue.splice(0, ACTIVITY_BATCH_MAX);
  const leftover = activityQueue.splice(0);
  activityQueue = leftover;
  const first = batch[0] || {};
  try {
    await axios.post(`${baseLink()}/record-state-portal-section-usage-batch`, {
      events: batch,
      state: first.state,
      organizationId: first.organizationId,
      organizationName: first.organizationName,
      sessionId: first.sessionId,
      submittedBy: first.submittedBy,
    });
  } catch (error) {
    console.error('Error flushing state portal activity batch:', error);
    // Drop failed batch rather than blocking UI / retry-loop forever.
  }
  if (activityQueue.length) scheduleActivityFlush();
}

export function enqueueActivityEvent({
  mainStore = {},
  event = 'ui_click',
  section = 'unknown',
  subsection = '',
  surface = 'state-portal-shell',
  label = '',
  path = '',
  targetTag = '',
  targetRole = '',
  targetText = '',
  targetId = '',
  targetHref = '',
  targetName = '',
  fromState = '',
  toState = '',
  state = '',
  organizationId = '',
  organizationName = '',
  meta = null,
} = {}) {
  const context = portalContext(mainStore, {
    state,
    organizationId,
    organizationName,
  });
  const entry = {
    event: optionalString(event, 40).toLowerCase() || 'ui_click',
    section: optionalString(section, 80).toLowerCase() || 'unknown',
    subsection: optionalString(subsection, 80).toLowerCase(),
    surface: optionalString(surface, 80) || 'state-portal-shell',
    label: optionalString(label),
    path: optionalString(path, 240),
    targetTag: optionalString(targetTag, 40),
    targetRole: optionalString(targetRole, 40),
    targetText: optionalString(targetText, 160),
    targetId: optionalString(targetId, 120),
    targetHref: optionalString(targetHref, 240),
    targetName: optionalString(targetName, 120),
    fromState: optionalString(fromState, 40).toUpperCase(),
    toState: optionalString(toState, 40).toUpperCase(),
    sessionId: getStatePortalAnalyticsSessionId(),
    meta: meta && typeof meta === 'object' ? meta : null,
    createdAt: new Date().toISOString(),
    ...context,
  };
  activityQueue.push(entry);
  if (activityQueue.length >= ACTIVITY_BATCH_MAX) {
    if (activityFlushTimer) {
      clearTimeout(activityFlushTimer);
      activityFlushTimer = null;
    }
    flushActivityQueue();
  } else {
    scheduleActivityFlush();
  }
  return { recorded: true, queued: true };
}

export async function trackStateSwitch({
  mainStore = {},
  fromState = '',
  toState = '',
  organizationId = '',
  organizationName = '',
  section = '',
  subsection = '',
} = {}) {
  const from = optionalString(fromState, 40).toUpperCase();
  const to = optionalString(toState, 40).toUpperCase();
  if (!to || from === to) return { recorded: false, skipped: true };
  return enqueueActivityEvent({
    mainStore,
    event: 'state_switch',
    section: optionalString(section, 80).toLowerCase() || 'dashboard',
    subsection,
    surface: 'state-portal-shell',
    label: from ? `${from} → ${to}` : `Switch to ${to}`,
    fromState: from,
    toState: to,
    state: to,
    organizationId,
    organizationName,
    path: typeof window !== 'undefined' ? window.location?.pathname : '',
  });
}

function readableText(el) {
  if (!el) return '';
  const aria = optionalString(el.getAttribute?.('aria-label'), 160);
  if (aria) return aria;
  const title = optionalString(el.getAttribute?.('title'), 160);
  if (title) return title;
  const text = optionalString(el.innerText || el.textContent, 160)
    .replace(/\s+/g, ' ');
  return text;
}

function findInteractiveTarget(start) {
  let node = start;
  let depth = 0;
  while (node && node !== document.body && depth < 12) {
    if (node.nodeType === 1) {
      const tag = String(node.tagName || '').toLowerCase();
      const role = optionalString(node.getAttribute?.('role'), 40).toLowerCase();
      const clickable = Boolean(
        tag === 'button'
        || tag === 'a'
        || tag === 'summary'
        || tag === 'label'
        || tag === 'input'
        || tag === 'select'
        || tag === 'textarea'
        || tag === 'option'
        || role === 'button'
        || role === 'tab'
        || role === 'menuitem'
        || role === 'link'
        || role === 'checkbox'
        || role === 'radio'
        || role === 'switch'
        || node.hasAttribute?.('tabindex')
        || typeof node.onclick === 'function'
        || node.getAttribute?.('data-analytics-label')
      );
      if (clickable) return node;
    }
    node = node.parentElement;
    depth += 1;
  }
  return start?.nodeType === 1 ? start : null;
}

function describeClickTarget(rawTarget) {
  const el = findInteractiveTarget(rawTarget);
  if (!el) return null;
  const tag = String(el.tagName || '').toLowerCase();
  const role = optionalString(el.getAttribute?.('role'), 40).toLowerCase();
  const isTab = role === 'tab'
    || optionalString(el.getAttribute?.('aria-selected'), 10) !== ''
    || /\btab\b/i.test(optionalString(el.className, 120));
  return {
    event: isTab ? 'tab_click' : 'ui_click',
    targetTag: tag,
    targetRole: role,
    targetText: readableText(el),
    targetId: optionalString(el.id, 120),
    targetHref: optionalString(el.getAttribute?.('href'), 240),
    targetName: optionalString(el.getAttribute?.('name') || el.getAttribute?.('data-analytics-label'), 120),
    label: readableText(el) || optionalString(el.id, 120) || tag,
  };
}

/**
 * Install capture-phase click tracking inside the State Portal shell.
 * getContext() should return { section, subsection, surface, state, label }.
 */
export function installStatePortalActivityCapture({
  mainStore = null,
  rootEl = null,
  getContext = () => ({}),
} = {}) {
  if (typeof window === 'undefined' || activityCaptureInstalled) {
    activityCaptureOptions = { mainStore, rootEl, getContext };
    return () => {};
  }
  activityCaptureInstalled = true;
  activityCaptureOptions = { mainStore, rootEl, getContext };

  activityClickHandler = (event) => {
    try {
      const opts = activityCaptureOptions || {};
      const root = opts.rootEl;
      const target = event?.target;
      if (root && target && typeof root.contains === 'function' && !root.contains(target)) {
        return;
      }
      const described = describeClickTarget(target);
      if (!described) return;
      const ctx = typeof opts.getContext === 'function' ? (opts.getContext() || {}) : {};
      enqueueActivityEvent({
        mainStore: opts.mainStore,
        event: described.event,
        section: ctx.section || 'unknown',
        subsection: ctx.subsection || '',
        surface: ctx.surface || 'state-portal-shell',
        label: described.label || ctx.label || '',
        path: typeof window !== 'undefined' ? window.location?.pathname : '',
        targetTag: described.targetTag,
        targetRole: described.targetRole,
        targetText: described.targetText,
        targetId: described.targetId,
        targetHref: described.targetHref,
        targetName: described.targetName,
        state: ctx.state || '',
        meta: {
          x: Number(event?.clientX) || 0,
          y: Number(event?.clientY) || 0,
        },
      });
    } catch (_) {
      /* never break UI */
    }
  };

  document.addEventListener('click', activityClickHandler, true);

  return () => {
    uninstallStatePortalActivityCapture();
  };
}

export function uninstallStatePortalActivityCapture() {
  if (activityClickHandler) {
    document.removeEventListener('click', activityClickHandler, true);
  }
  activityClickHandler = null;
  activityCaptureInstalled = false;
  activityCaptureOptions = null;
  if (activityFlushTimer) {
    clearTimeout(activityFlushTimer);
    activityFlushTimer = null;
  }
  flushActivityQueue();
}

export default {
  recordFeatureInterest,
  uploadFeatureInterestFile,
  getFeatureInterestForUser,
  trackSectionEnter,
  trackStateSwitch,
  enqueueActivityEvent,
  installStatePortalActivityCapture,
  uninstallStatePortalActivityCapture,
  getStatePortalAnalyticsSessionId,
};
